import { downloadTextFile, rowsToCsv } from '@/api/csvExport';
import { automationApi, type AutomationExecution } from '@/api/automationApi';
import { formatEvaluationMetrics, formatEvaluationSummary } from '@/utils/automationExecutionChange';

export const AUTOMATION_EXECUTION_EXPORT_PAGE_SIZE = 200;
export const AUTOMATION_EXECUTION_EXPORT_SOFT_CAP = 10_000;

export const AUTOMATION_EXECUTION_CSV_HEADERS = [
  'Rule',
  'Entity',
  'Entity type',
  'Entity key',
  'Action',
  'Before',
  'After',
  'Status',
  'Error',
  'Time',
  'Dry run',
  'Condition',
  'Metrics',
  'Revert status',
  'Revert at',
  'Reverted at',
  'Revert error',
] as const;

export type AutomationExecutionExportResult = {
  items: AutomationExecution[];
  truncated: boolean;
  total: number;
};

function jsonCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  return JSON.stringify(value);
}

export function executionToCsvRow(
  row: AutomationExecution
): (string | number | null | undefined)[] {
  return [
    row.ruleName || row.ruleId,
    row.entityName || row.entityKey,
    row.entityType,
    row.entityKey,
    row.actionType,
    jsonCell(row.beforeValue),
    jsonCell(row.afterValue),
    row.status,
    row.errorMessage ?? '',
    row.executionTime ?? '',
    row.dryRun ? 'true' : 'false',
    row.evaluationSummary || formatEvaluationSummary(row.evaluation),
    formatEvaluationMetrics(row.evaluation),
    row.revertStatus ?? '',
    row.revertAt ?? '',
    row.revertedAt ?? '',
    row.revertError ?? '',
  ];
}

export function automationExecutionsFilename(ruleId?: string): string {
  const trimmed = ruleId?.trim();
  if (trimmed) return `automation-executions_${trimmed}.csv`;
  return 'automation-executions.csv';
}

export function downloadAutomationExecutionsCsv(
  items: AutomationExecution[],
  options?: { ruleId?: string }
): void {
  const csv = rowsToCsv(
    [...AUTOMATION_EXECUTION_CSV_HEADERS],
    items.map(executionToCsvRow)
  );
  downloadTextFile(automationExecutionsFilename(options?.ruleId), csv);
}

export async function fetchAllAutomationExecutions(args: {
  profileId: string;
  ruleId?: string;
  outcome?: string;
  scheduledSlot?: string;
  softCap?: number;
}): Promise<AutomationExecutionExportResult> {
  const softCap = args.softCap ?? AUTOMATION_EXECUTION_EXPORT_SOFT_CAP;
  const items: AutomationExecution[] = [];
  let offset = 0;
  let total = 0;
  let truncated = false;

  while (items.length < softCap) {
    const res = await automationApi.listExecutions(args.profileId, {
      ...(args.ruleId ? { rule_id: args.ruleId } : {}),
      ...(args.outcome ? { outcome: args.outcome } : {}),
      ...(args.scheduledSlot ? { scheduled_slot: args.scheduledSlot } : {}),
      limit: AUTOMATION_EXECUTION_EXPORT_PAGE_SIZE,
      offset,
    });
    if (!res.success) {
      throw new Error(res.message || 'Failed to export executions');
    }
    const pageItems = res.data?.items ?? [];
    total = res.data?.total ?? pageItems.length;
    items.push(...pageItems);
    if (pageItems.length < AUTOMATION_EXECUTION_EXPORT_PAGE_SIZE || items.length >= total) {
      break;
    }
    offset += AUTOMATION_EXECUTION_EXPORT_PAGE_SIZE;
    if (items.length >= softCap && items.length < total) {
      truncated = true;
      break;
    }
  }

  return {
    items: items.slice(0, softCap),
    truncated: truncated || (total > softCap && items.length >= softCap),
    total,
  };
}
